import React, { useRef, useState, FunctionComponent } from 'react';
import {
  Link,
  Grid,
  Card,
  TextField,
  Select,
  MenuItem,
  TableBody,
  Table,
  TableRow,
  FormControl,
  InputLabel,
  TableCell,
  TableContainer,
  SelectChangeEvent,
  FormHelperText,
  Box,
  Container,
  Typography,
  Button,
} from '@mui/material';
import Logo from 'components/Logo';
import Page from 'components/Page';
import useResponsive from 'hooks/useResponsive';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import useSettings from 'hooks/useSettings';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axiosInstance from 'utils/axios';
import CardInput from './CardInput';
import usePortalId from 'hooks/portal/usePortalId';
import { BillingDetail } from '../PaymentType';
import _ from 'lodash';
import {
  BILLING_CONFIRM_PAYMENT_API_URL,
  BILLING_START_API_URL,
} from 'api/apiConstants';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import SubscriptionCancel from './SubscriptionCancel';
import { PATH_USER, ROOT_NAVIGATION, SUPPORT_URL } from 'routes/paths';
import useAuth from '../../../../src/hooks/useAuth';
import AuthHeader from 'components/auth/AuthHeader';
import { useTranslation } from 'react-i18next';
const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(25, 0, 0),
}));

interface PaymentType {
  full_name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  amount: string;
  plan_id: string;
}

type Props = {
  billingDetails: BillingDetail;
};

const initialErrorStateData = {
  full_name: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  amount: '',
  card: '',
};

export const Payments: FunctionComponent<Props> = ({ billingDetails }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const portalId = usePortalId();
  const stripe = useStripe();
  const elements = useElements();
  const smUp = useResponsive('up', 'sm');
  const { themeStretch } = useSettings();
  const isErrorRef = useRef(false);
  const [stateError, setStateError] = useState(initialErrorStateData);
  const [cardError, setCardError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [payment, setPayment] = useState<PaymentType>({
    full_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'select',
    amount: billingDetails.plans[0].price,
    plan_id: billingDetails.plans[0]._id,
  });

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    setPayment({ ...payment, [e.target.name]: e.target.value });
  };

  const validateForm = (
    validateFormData: PaymentType,
    amountToBePaid: string
  ) => {
    const validate = {
      full_name: (full_name: string) =>
        full_name.trim().length > 0 ? true : t('name_is_required'),
      phone: (phone: number) =>
        phone
          .toString()
          .trim()
          .match(
            /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*/
          )
          ? true
          : t('phone_number_not_valid'),
      email: (email: string) =>
        email
          .trim()
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          )
          ? true
          : t('email_must_be_a_valid_email_address'),
      address: (address: string) =>
        address.trim().length > 0 ? true : t('address_is_required'),
      city: (city: string) =>
        city.trim().length > 0 ? true : t('city_is_required'),
      state: (state: string) =>
        state.trim().length > 0 ? true : t('state_is_required'),
      zip: (zip: string) =>
        zip.trim().length > 0 ? true : t('zip_is_required'),
      country: (country: string) =>
        country !== 'select' ? true : t('country_is_required'),
      amount: (amount: string) =>
        amount === amountToBePaid ? true : t('amount_does_not_match_with_plan'),
      plan_id: () => true,
    };
    const errObj = {
      ...initialErrorStateData,
    };
    let errorFieldsVar = false;
    _.forIn(validateFormData, (value, key) => {
      const error = validate[key as keyof typeof validate](value as never);
      if (error !== true) {
        errorFieldsVar = true;
      }
      // @ts-ignore
      errObj[key] = error ?? '';
    });
    isErrorRef.current = errorFieldsVar;
    setStateError({ ...stateError, ...errObj });
  };

  const handleSubmitSub = async (event: any) => {
    validateForm(payment, billingDetails.plans[0].price);

    if (!stripe || !elements || isErrorRef.current) {
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (cardElement !== null) {
      setIsSubmitting(true);
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          address: {
            city: payment.city,
            country: payment.country,
            line1: payment.address,
            postal_code: payment.zip,
            state: payment.state,
          },
          email: payment.email,
          name: payment.full_name,
          phone: payment.phone,
        },
      });
      if (result.error) {
        setIsSubmitting(false);
        isErrorRef.current = true;
        setCardError(result.error.message ?? '');
      } else {
        try {
          setIsSubmitting(true);
          const res = await axiosInstance.post(BILLING_START_API_URL, {
            payment_method: result.paymentMethod.id,
            email: payment.email,
            plan_id: payment.plan_id,
            portal_id: portalId,
          });
          // eslint-disable-next-line camelcase
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { client_secret, status } = res.data.data;
          if (status === 'requires_action') {
            setIsSubmitting(true);
            stripe
              .confirmCardPayment(client_secret)
              .then(async function (result) {
                if (result.error) {
                  setIsSubmitting(false);
                  enqueueSnackbar(t('issue_payment_failed'), {
                    variant: 'error',
                  });
                } else {
                  try {
                    await axiosInstance.post(BILLING_CONFIRM_PAYMENT_API_URL, {
                      payment_intent: result.paymentIntent.id,
                      portal_id: portalId,
                    });
                    enqueueSnackbar(
                      t('payment_successful_redirect_to_dashboard'),
                      { variant: 'success' }
                    );
                    navigate(
                      PATH_USER.general.accountSettings(portalId, 'billing')
                    );
                  } catch (error: any) {
                    enqueueSnackbar(t('issue_payment_failed'), {
                      variant: 'error',
                    });
                  }
                }
              });
          } else {
            setIsSubmitting(false);
            enqueueSnackbar(t('payment_successful_redirect_to_dashboard'), {
              variant: 'success',
            });
            navigate(PATH_USER.general.accountSettings(portalId, 'billing'));
          }
        } catch (error: any) {
          setIsSubmitting(false);
          enqueueSnackbar(error.message, {
            variant: 'error',
          });
          navigate('/');
        }
      }
    }
  };

  return (
    <Page title={t('payment')}>
      <RootStyle>
        <AuthHeader>
          {smUp && (
            <>
              <Typography variant='body2' sx={{ mr: { md: 2 } }}>
                {t('need_help')}? {''}
                <Link
                  target='_blank'
                  variant='subtitle2'
                  color='#2196F3'
                  href={SUPPORT_URL}
                >
                  {t('support')}
                </Link>
                <Link
                  onClick={logout}
                  variant='subtitle2'
                  color='#2196F3'
                  component={RouterLink}
                  to=''
                >
                  {' | '}
                  {t('logout')}
                </Link>
              </Typography>
            </>
          )}
        </AuthHeader>
        {new Date(Number(billingDetails.active_plan.validity)) <= new Date() ||
        billingDetails.active_plan.plan_type === 'trial' ||
        !billingDetails.is_subscribed ? (
          <Container
            maxWidth={themeStretch ? false : 'lg'}
            sx={{ paddingBottom: '25px' }}
          >
            <ContentStyle>
              <Box>
                <Grid container spacing={8}>
                  <Grid item xs={12} md={8}>
                    <Button
                      sx={{ marginBottom: '15px' }}
                      variant='outlined'
                      href={ROOT_NAVIGATION.general.home(portalId)}
                    >
                      {t('back')}
                    </Button>
                    <Grid
                      container
                      columnSpacing={10}
                      direction='column'
                      spacing={4}
                    >
                      <Grid item xs={8}>
                        <Card sx={{ p: 3 }}>
                          <Typography
                            sx={{ margin: '15px 0px 15px 0px' }}
                            variant='subtitle2'
                            color='text secondary'
                          >
                            {t('billing_details')}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              rowGap: 3,
                              columnGap: 2,
                              gridTemplateColumns: {
                                xs: 'repeat(1, 1fr)',
                                sm: 'repeat(2, 1fr)',
                              },
                            }}
                          >
                            <TextField
                              id='outlined-basic'
                              name='full_name'
                              label={t('full_name')}
                              variant='outlined'
                              value={payment.full_name}
                              onChange={handleChange}
                              error={stateError.full_name.length ? true : false}
                              helperText={stateError.full_name}
                            />
                            <TextField
                              id='outlined-basic'
                              type='number'
                              name='phone'
                              label={t('phone')}
                              variant='outlined'
                              value={payment.phone}
                              onChange={handleChange}
                              error={stateError.phone.length ? true : false}
                              helperText={stateError.phone}
                            />
                            <TextField
                              id='outlined-basic'
                              name='email'
                              label={t('email_address')}
                              variant='outlined'
                              value={payment.email}
                              onChange={handleChange}
                              error={stateError.email.length ? true : false}
                              helperText={stateError.email}
                            />
                            <TextField
                              id='outlined-basic'
                              name='address'
                              label={t('address')}
                              variant='outlined'
                              value={payment.address}
                              onChange={handleChange}
                              error={stateError.address.length ? true : false}
                              helperText={stateError.address}
                            />
                            <TextField
                              id='outlined-basic'
                              name='city'
                              label={t('city')}
                              variant='outlined'
                              value={payment.city}
                              onChange={handleChange}
                              error={stateError.city.length ? true : false}
                              helperText={stateError.city}
                            />
                            <TextField
                              id='outlined-basic'
                              name='state'
                              label={t('state')}
                              variant='outlined'
                              value={payment.state}
                              onChange={handleChange}
                              error={stateError.state.length ? true : false}
                              helperText={stateError.state}
                            />
                            <TextField
                              id='outlined-basic'
                              name='zip'
                              label={t('zip')}
                              variant='outlined'
                              value={payment.zip}
                              onChange={handleChange}
                              error={stateError.zip.length ? true : false}
                              helperText={stateError.zip}
                            />
                            <FormControl fullWidth>
                              <InputLabel id='demo-simple-select-label'>
                                {t('country')}
                              </InputLabel>
                              <Select
                                sx={{ width: '50%' }}
                                id='demo-simple-select-helper'
                                label={t('country')}
                                name='country'
                                value={payment.country}
                                onChange={handleChange}
                                error={stateError.country.length ? true : false}
                              >
                                <MenuItem key='select' value={'select'}>
                                  {t('select_country')}
                                </MenuItem>
                                {_.map(
                                  billingDetails.countries,
                                  function (value, key) {
                                    return (
                                      <MenuItem key={key} value={key}>
                                        {value}
                                      </MenuItem>
                                    );
                                  }
                                )}
                              </Select>
                              {stateError.country.length > 0 && (
                                <FormHelperText error={true}>
                                  {stateError.country}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Box>
                        </Card>
                      </Grid>
                      <Grid item xs={4}>
                        <Card sx={{ p: 3 }}>
                          <Typography
                            sx={{ margin: '15px 0px 15px 0px' }}
                            variant='subtitle2'
                            color='text secondary'
                          >
                            {t('payment_info')}
                          </Typography>

                          <CardInput />
                          {cardError.length > 0 && (
                            <FormHelperText error={true}>
                              {cardError}
                            </FormHelperText>
                          )}
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Grid
                      container
                      columnSpacing={10}
                      direction='column'
                      spacing={4}
                    >
                      <Grid item xs={8}>
                        <Card sx={{ p: 3 }}>
                          <Typography
                            sx={{ margin: '15px 0px 15px 0px' }}
                            variant='subtitle2'
                            color='text secondary'
                          >
                            {t('plan')}
                          </Typography>
                          <div>
                            <Logo sx={{ width: '100%', textAlign: 'center' }} />
                            <Typography
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                paddingTop: '30px',
                              }}
                              variant='h4'
                            >
                              ${billingDetails.plans[0].price}/{t('month')}
                            </Typography>
                            <Typography
                              color='error'
                              variant='subtitle1'
                              sx={{
                                opacity: 0.72,
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              {t('saving_per_month')}
                            </Typography>
                          </div>
                        </Card>
                      </Grid>
                      <Grid item xs={4}>
                        <Card sx={{ p: 3 }}>
                          <Typography
                            sx={{ margin: '15px 0px 15px 0px' }}
                            variant='subtitle2'
                            color='text secondary'
                          >
                            {t('order_summary')}
                          </Typography>
                          <TableContainer>
                            <Table>
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <Typography
                                      variant='subtitle2'
                                      color='#637381'
                                    >
                                      {t('sub_total')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      color='#212B36'
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'row-reverse',
                                      }}
                                      variant='subtitle2'
                                    >
                                      ${billingDetails.plans[0].price}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    <Typography variant='h5'>
                                      {t('total')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      color='#212B36'
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'row-reverse',
                                      }}
                                      variant='subtitle2'
                                    >
                                      ${billingDetails.plans[0].price}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Card>
                      </Grid>
                      <Grid item xs={4}>
                        <Card sx={{ mb: 3 }}>
                          <div className='save-button'>
                            <LoadingButton
                              type='button'
                              fullWidth
                              variant='contained'
                              loading={isSubmitting}
                              onClick={handleSubmitSub}
                            >
                              {t('complete_payment')}
                            </LoadingButton>
                          </div>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </ContentStyle>
          </Container>
        ) : (
          <SubscriptionCancel
            activePlan={billingDetails.active_plan}
            isSubscribed={billingDetails.is_subscribed}
          ></SubscriptionCancel>
        )}
      </RootStyle>
    </Page>
  );
};
